import React from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useGetEventDetailsHandler } from '../dataHandlers/getEventDetailsHandler';
import EventSummaryHeader from './EventSummaryHeader';
import DeliveryAttemptsSection from './DeliveryAttemptsSection';

/**
 * EventDetailPane component displays event details in the right panel
 * @param {string} eventId - The event ID to display
 */
const EventDetailPane = ({ eventId }) => {
  const {
    eventDetails,
    isLoading: isLoadingDetails,
    isError: isErrorDetails,
    error: errorDetails,
  } = useGetEventDetailsHandler({ eventId });

  // Show loading state only if event details are loading
  if (isLoadingDetails) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading event details...
        </Typography>
      </Box>
    );
  }

  // Show error state only if event details failed
  if (isErrorDetails) {
    const errorMessage = errorDetails?.response?.status === 404 ? 'Event not found' : 'Failed to load event details';

    return (
      <Box sx={{ py: 3 }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  // Show empty state if no event details
  if (!eventDetails || !eventDetails.event) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No event details available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 3,
        py: 2.5,
      }}
    >
      {/* Section 1: Event Summary */}
      <EventSummaryHeader eventDetails={eventDetails} />

      {/* Section 2: Delivery Attempts */}
      <DeliveryAttemptsSection eventId={eventId} />
    </Box>
  );
};

export default EventDetailPane;
